import React, { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { adminService } from '../../services/admin';
import { DepositAddress } from '../../types';
import { Card } from '../../components/ui/Button';
import { Button } from '../../components/ui/Button';
import { Input, Select, Alert } from '../../components/ui/Modal';
import { gs } from '../../styles/globalStyles';
import { NETWORKS } from '../../utils/constants';
import toast from 'react-hot-toast';
import { Ico } from '../../components/ui/Icon';

const COINS = ['BTC', 'ETH', 'USDT', 'XRP', 'SOL', 'BNB', 'ADA', 'DOGE', 'DOT'];

const CryptoAddresses: React.FC = () => {
  const [addresses, setAddresses] = useState<Record<string, DepositAddress[]>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newAddr, setNewAddr] = useState({ cryptocurrency: 'BTC', network: 'Native', address: '', label: '' });
  const [previewAddress, setPreviewAddress] = useState('');

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminService.getDepositAddresses();
      setAddresses(res.data.deposit_addresses || {});
    } catch {} finally { setLoading(false); }
  };

  const saveAddress = async () => {
    if (!newAddr.address) { toast.error('Address is required'); return; }
    setSaving(true);
    try {
      await adminService.setDepositAddress(newAddr);
      toast.success('Deposit address saved!');
      load();
      setNewAddr({ cryptocurrency: 'BTC', network: 'Native', address: '', label: '' });
    } catch { toast.error('Failed to save'); } finally { setSaving(false); }
  };

  const deleteAddress = async (id: number) => {
    if (!window.confirm('Delete this address?')) return;
    try {
      await adminService.deleteDepositAddress(id);
      toast.success('Address deleted');
      load();
    } catch { toast.error('Failed'); }
  };

  const availableNetworks = NETWORKS[newAddr.cryptocurrency] || ['Native'];

  return (
    <div className="p-4 md:p-6">
      {/* Add new */}
      <Card className="mb-6">
        <div className="flex flex-col items-center gap-2 mb-5 pb-4 border-b border-gray-800/40">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <Ico name="add" className="w-5 h-5 text-primary" />
          </div>
          <h3 className="font-display font-semibold text-white">Add / Update Deposit Address</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <Select
            label="Cryptocurrency"
            options={COINS.map(c => ({ value: c, label: c }))}
            value={newAddr.cryptocurrency}
            onChange={e => setNewAddr(p => ({ ...p, cryptocurrency: e.target.value, network: NETWORKS[e.target.value]?.[0] || 'Native' }))}
          />
          <Select
            label="Network"
            options={availableNetworks.map((n: string) => ({ value: n, label: n }))}
            value={newAddr.network}
            onChange={e => setNewAddr(p => ({ ...p, network: e.target.value }))}
          />
          <Input
            label="Wallet Address"
            placeholder="Paste the deposit wallet address here"
            value={newAddr.address}
            onChange={e => setNewAddr(p => ({ ...p, address: e.target.value }))}
          />
          <Input
            label="Label (optional)"
            placeholder="e.g. Main USDT TRC20 wallet"
            value={newAddr.label}
            onChange={e => setNewAddr(p => ({ ...p, label: e.target.value }))}
          />
        </div>

        {/* QR Preview */}
        {newAddr.address && (
          <div className="flex items-center gap-4 p-4 bg-gray-800/40 rounded-xl mb-4">
            <div className="bg-white p-2 rounded-lg">
              <QRCodeSVG value={newAddr.address} size={80} />
            </div>
            <div>
              <p className="text-xs text-gray-400 mb-1">QR Preview</p>
              <p className="font-mono text-primary text-xs break-all">{newAddr.address}</p>
            </div>
          </div>
        )}

        <Button onClick={saveAddress} loading={saving} icon={<Ico name="save" />}>
          Save Address
        </Button>
      </Card>

      {/* Existing addresses */}
      <h3 className="font-display font-semibold text-white mb-4">Configured Addresses</h3>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin w-6 h-6 border-2 border-primary/20 border-t-primary rounded-full" />
        </div>
      ) : Object.keys(addresses).length === 0 ? (
        <Alert type="info" message="No deposit addresses configured yet. Add one above." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Object.entries(addresses).map(([coin, coinAddresses]) =>
            coinAddresses.map(addr => (
              <Card key={addr.id} className="relative">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="font-display font-bold text-white">{coin}</span>
                    <span className="text-xs px-2 py-0.5 bg-gray-800 rounded-full font-mono text-gray-400">{addr.network}</span>
                    {addr.is_active && <span className="text-xs px-2 py-0.5 bg-primary/15 text-primary rounded-full font-mono">Active</span>}
                  </div>
                  <button onClick={() => deleteAddress(addr.id)} className="p-1.5 text-gray-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-all">
                    <Ico name="delete" />
                  </button>
                </div>

                <div className="flex items-center gap-3">
                  <div className="bg-white p-2 rounded-lg shrink-0">
                    <QRCodeSVG value={addr.address} size={60} />
                  </div>
                  <div>
                    {addr.label && <p className="text-gray-400 text-xs mb-1">{addr.label}</p>}
                    <p className="font-mono text-primary text-xs break-all">{addr.address}</p>
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>
      )}
    </div>
  );
};

export default CryptoAddresses;
